<template>
    <div class="h-full flex flex-col">
        <div v-if="contact" class="n-max-width">
            <div class="w-full py-2 flex justify-between">
                <h2 class="pl-2 text-xl font-semibold">
                    <span>{{ $enum.ContactTitle.translate(contact.title) }}&nbsp;</span>
                    <span>{{ contact.firstname }}&nbsp;</span>
                    <span>{{ contact.lastname }}&nbsp;</span>
                    <span v-if="contact.birthname">({{ contact.birthname }})&nbsp;</span>
                    <span v-if="contact.referrer">|&nbsp;{{ contact.referrer }}</span>
                </h2>
                <div class="p-1">
                    <span v-if="contact.moodleSuspended" title="Plateforme fermée">
                        <LockClosedIcon class="w-4 h-4 text-red" />
                    </span>
                    <span v-else title="Plateforme ouverte">
                        <LockOpenIcon class="w-4 h-4 text-green" />
                    </span>
                </div>
            </div>
            <Transition name="fadeHeight">
                <div class="flex flex-wrap justify-between" v-if="!$store.settings.contactHideDetail">
                    <div class="flex-grow flex items-center">
                        <Picture :size="9" :src="contact.photo" />
                        <div class="pl-4">
                            <div v-if="contact.email">
                                <EnvelopeIcon class="w-6 pr-2 inline-block" />
                                <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
                            </div>
                            <div v-if="contact.cellphone" title="mobile">
                                <PhoneIcon class="w-6 pr-2 inline-block" />
                                <a :href="`tel:${contact.cellphone}`">{{ contact.cellphone }}</a>
                            </div>
                            <div v-if="contact.phone" title="téléphone">
                                <PhoneIcon class="w-6 pr-2 inline-block" />
                                <a :href="`tel:${contact.phone}`">{{ contact.phone }}</a>
                            </div>
                            <div v-if="contact.address">
                                <MapPinIcon class="w-6 pr-2 inline-block" />
                                <span>{{ contact.address }}</span>
                            </div>
                            <div v-if="contact.birthdate" :title="$format.date(contact.birthdate)">
                                <CalendarIcon class="w-6 pr-2 inline-block" />
                                <span>{{ $format.differenceInYears(contact.birthdate) }}&nbsp;ans</span>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow flex flex-col justify-center items-end text-sm gap-y-0.5">
                        <div>
                            <span>Dernière connexion Moodle : </span>
                            <span v-if="contact.moodleLastAccess">
                                {{ $format.distanceToNow(contact.moodleLastAccess) }}
                            </span>
                            <span v-else>jamais</span>
                        </div>
                        <div>
                            <span>Dernière connexion MyEdvenn : </span>
                            <span v-if="contact.myedvennLastAccess">
                                {{ $format.distanceToNow(contact.myedvennLastAccess) }}
                            </span>
                            <span v-else>jamais</span>
                        </div>
                        <div v-if="contact.adviser">
                            <span>Conseiller(e) : </span>
                            <UserIcon :user="contact.adviser" class="inline-block" :size="1.5" />
                        </div>
                        <div v-if="contact.mentor">
                            <span>Référent(e) : </span>
                            <UserIcon :user="contact.mentor" class="inline-block" :size="1.5" />
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
        <div v-else class="n-max-width text-white flex items-center justify-center n-loading-background">
            <ArrowPathIcon class="animate-spin inline-block h-20" />
        </div>
        <!-- menu -->
        <div class="bg-white">
            <div class="border-b overflow-x-auto">
                <div class="max-w-7xl mx-auto w-full flex">
                    <!-- contact - home -->
                    <template v-if="$rights.isRoleManager || $rights.isRoleAdviser || $rights.isRoleMentor">
                        <NuxtLink class="px-2 sm:px-4 py-1 translate-y-0.5 hover:shadow-inner rounded-t-md"
                            exact-active-class="cursor-default border-x border-t bg-white"
                            :to="{ name: 'admin-contact-contactId', params: { contactId: contact?.id } }">
                            <HomeIcon class="w-5 h-5 text-gray-500" />
                        </NuxtLink>
                    </template>
                    <!-- contact - packages -->
                    <template v-for="pf of packageLinks">
                        <NuxtLink
                            class="px-2 sm:px-4 pb-1 translate-y-0.5 hover:shadow-inner rounded-t-md text-center whitespace-nowrap"
                            :to="pf.href" @click="scrollTop"
                            exact-active-class="font-semibold border-x border-t hover:shadow-none cursor-default">
                            {{ pf.title }}
                        </NuxtLink>
                    </template>
                    <!-- hide - contact details -->
                    <div class="flex-grow"></div>
                    <div class="border-x border-t bg-white rounded-t-md px-2 sm:px-4 cursor-pointer align-middle translate-y-0.5"
                        @click="$store.settings.contactHideDetail = !$store.settings.contactHideDetail">
                        <ChevronDownIcon class="w-6 inline-block cursor-pointer transition-all ease-in-out"
                            :class="{ 'rotate-180': !$store.settings.contactHideDetail }" />
                    </div>
                </div>
            </div>
            <div class="border-t mt-1 mb-2 px-4 overflow-x-auto">
                <div class="flex mt-0.5 w-full max-w-7xl mx-auto pb-1">
                    <NuxtLink v-for="packageLink of packageSubLinks" @click="scrollTop"
                        exact-active-class="hover:shadow-none font-semibold border-b border-x cursor-default bg-white"
                        class="px-4 py-1 rounded-b-md hover:shadow -translate-y-[4px] text-center whitespace-nowrap"
                        :to="packageLink.href">
                        {{ packageLink.title }}
                    </NuxtLink>
                </div>
            </div>
        </div>
        <div class="h-full grow overflow-y-scroll"
            v-if="contact && ($route.name === 'admin-contact-contactId' || packageFile)">
            <div id="package_file_top"></div>
            <RouterView :contactId="contact.id" :packageFileId="packageFile?.id"
                :contactPackageId="packageFile?.contactPackage?.id" />
        </div>
    </div>
</template>

<script lang="ts">
import { ArrowPathIcon, LockClosedIcon, LockOpenIcon, MapPinIcon, EnvelopeIcon, PhoneIcon, ChevronDownIcon, HomeIcon, CalendarIcon } from '@heroicons/vue/24/outline'
import type { RoutesNamedLocationsResolved } from '@typed-router/__routes';

import type { IContact } from '@/api/myedvenn/contact'
import type { IPackageFile } from '~/api/myedvenn/package_file';
import { scrollTo } from '~/util';

export default defineComponent({
    components: { ArrowPathIcon, LockClosedIcon, LockOpenIcon, MapPinIcon, EnvelopeIcon, PhoneIcon, ChevronDownIcon, HomeIcon, CalendarIcon },
    setup() {
        useSeoMeta({ title: 'My Edvenn - Admin - Contact' })

        const { $api, $router, $rights, $routeParser, $watchRoute } = useNuxtApp()

        const contact = ref<IContact>()
        const packageFile = ref<IPackageFile>()
        const packageFiles = ref<IPackageFile[]>([])

        $watchRoute(async _route => {
            const _contactId = $routeParser.params.int.get('contactId')

            const _packageFileId = $routeParser.params.int.getOpt('packageFileId')
            const _contactPackageId = $routeParser.query.int.getOpt('contactPackageId')
            const _packageTrainingId = $routeParser.query.int.getOpt('packageTrainingId')

            await Promise.all([
                $api.packageFile.find({
                    criterias: [{ field: 'contact.id', value: `${_contactId}` }],
                    relations: ['package.trainings', 'contactPackage.state'],
                })
                    .then(v => packageFiles.value = v.values),
                $api.contact.get({ id: _contactId })
                    .then(c => contact.value = c)
            ])

            if (_packageFileId)
                packageFile.value = packageFiles.value.find(pf => pf.id === _packageFileId)
            else if (_packageTrainingId) {
                const _packageFile = packageFiles.value.find(pf => pf.package.trainings.find(t => t.id === _packageTrainingId))
                if (_packageFile)
                    $router.replace({
                        name: 'admin-contact-contactId-training-packageFileId-pedagogical',
                        params: { contactId: _contactId, packageFileId: _packageFile.id.toString() },
                        hash: _route.hash
                    })
            }
            else if (_contactPackageId) {
                const _packageFile = packageFiles.value.find(pf => pf.contactPackage.id === _contactPackageId)
                if (_packageFile)
                    $router.replace({
                        name: 'admin-contact-contactId-training-packageFileId-pedagogical',
                        params: { contactId: _contactId, packageFileId: _packageFile.id.toString() },
                        hash: _route.hash
                    })
            } else {
                packageFile.value = undefined
            }

            if (!packageFile.value && _route.name !== 'admin-contact-contactId')
                $router.replace({
                    name: 'admin-contact-contactId',
                    params: { contactId: _contactId }
                })

        }, { immediate: true, pattern: /^\/admin\/contact\// })

        return {
            contact,
            packageFile,

            packageLinks: computed(() => {
                if (!contact.value) return []

                const currentRouteName = ((_r) => {
                    switch (_r) {
                        case 'admin-contact-contactId-training-packageFileId-pedagogical':
                        case 'admin-contact-contactId-training-packageFileId-history':
                        case 'admin-contact-contactId-training-packageFileId-invoice':
                        case 'admin-contact-contactId-training-packageFileId-administrative':
                            return _r
                        default:
                            return 'admin-contact-contactId-training-packageFileId-pedagogical'
                    }
                })($router.currentRoute.value.name.toString())

                return packageFiles.value.map<Link>(pf => ({
                    title: pf.package.ref,
                    sort: pf.id,
                    href: {
                        name: currentRouteName,
                        params: { contactId: contact.value.id.toString(), packageFileId: pf.id.toString() }
                    }
                }))
            }),

            packageSubLinks: computed(() => {
                if (!packageFile.value) return []

                const _histo: Link = {
                    title: 'Historique',
                    sort: 1,
                    href: {
                        name: 'admin-contact-contactId-training-packageFileId-history',
                        params: { contactId: contact.value.id.toString(), packageFileId: packageFile.value.id.toString() }
                    }
                }
                const _admin: Link = {
                    title: 'Administratif',
                    sort: 2,
                    href: {
                        name: 'admin-contact-contactId-training-packageFileId-administrative',
                        params: { contactId: contact.value.id.toString(), packageFileId: packageFile.value.id.toString() }
                    }
                }
                const _pedago: Link = {
                    title: 'Suivi pédagogique',
                    sort: 3,
                    href: {
                        name: 'admin-contact-contactId-training-packageFileId-pedagogical',
                        params: { contactId: contact.value.id.toString(), packageFileId: packageFile.value.id.toString() }
                    }
                }
                // const _invoice: Link = {
                //     title: 'Facturation',
                //     sort: 4,
                //     href: {
                //         name: 'admin-contact-contactId-training-packageFileId-invoice',
                //         params: { contactId: contact.value.id.toString(), packageFileId: packageFile.value.id.toString() }
                //     }
                // }

                const items = new Set<Link>()

                if ($rights.isRoleAdmin) {
                    items.add(_histo)
                    items.add(_admin)
                    items.add(_pedago)
                    // items.add(_invoice)
                }
                // if($rights.isRoleAdviser)
                // if($rights.isRoleMentor)
                // if($rights.isRoleManager)
                if ($rights.isRoleTrainer) {
                    items.add(_pedago)
                    items.add(_admin)
                }
                // if($rights.isRoleCanAccessInvoicing)
                // if($rights.isRoleMarketing)

                return [...items.values()].sort((a, b) => a.sort - b.sort)
            }),

            scrollTop(evt: Event) {
                const elt = evt.target as HTMLAnchorElement
                const isLinkActive = elt?.classList?.contains('router-link-active')
                if (isLinkActive) scrollTo('package_file_top')
            }
        }
    }
})

type Link = {
    title: string;
    sort: number;
    href: RoutesNamedLocationsResolved;
}

</script>

<style lang="postcss">
.fadeHeight-enter-active,
.fadeHeight-leave-active {
    transition: all 0.5s;
    max-height: 350px;
}

.fadeHeight-enter-from,
.fadeHeight-leave-to {
    opacity: 0;
    max-height: 0px;
}
</style>